import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TodayBar — the today contract + focus-block mode (`trd` family).
 * R2 — docs/WORKFLOW-ADVERSARIAL-REVIEW-2026-08.md W2, reworked per the
 * 2026-08-16 UX review.
 *
 * The mirror-not-wheel fix, in two behavioral levers that live where the
 * 9:04am moment actually happens:
 *
 *   THE CONTRACT  an unplanned morning shows a pick-list (a balanced slate
 *                 from the two-axis rank, each pill labeled with WHY it is
 *                 there); a planned one shows the picks with live done-state,
 *                 an Edit affordance (the backend upserts — the UI must not
 *                 be more rigid than its store), and the ≥1-proactive
 *                 warning when the contract was broken. Any open task can
 *                 join the plan via search — the slate suggests, it does
 *                 not gatekeep.
 *
 *   FOCUS MODE    "Focus" turns the app itself into the block: while it
 *                 runs, the host narrows every task board to proactive work
 *                 and the bar shows the countdown (25/50/90m presets). A
 *                 natural expiry gets an explicit end-moment; ending early
 *                 does not (the user chose to stop — don't celebrate it).
 *
 * The streak is DERIVED optimistically: when the plan shows a completed
 * proactive pick and the server says today hasn't counted yet
 * (`streak_today: false`), the flame shows streak+1 lit — no local state to
 * drift, and the refetch lands on the same number.
 *
 * Presentational + MUI-free. Focus state is session-local by design — a
 * reload ends the block, which is honest: the block was broken.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Flame, Loader2, Pencil, Play, Square, Sun, X } from 'lucide-react';
const isProactive = (t) => t.origin === 'self' || !!t.goal_id;
const FOCUS_PRESETS = [25, 50, 90];
const MAX_PICKS = 5;
const SEARCH_LIMIT = 6;
/** Why a suggestion is on the slate — the rank's reasoning, said out loud. */
function slateReason(t) {
    if ((t.tags || []).includes('quick-win'))
        return 'quick win';
    const r = t.rank;
    if ((r?.strategic || 0) >= 40)
        return 'strategic';
    if ((r?.urgency || 0) >= 40)
        return 'urgent';
    return null;
}
export function TodayBar({ data, loading, tasks, onSetToday, onComplete, onFocusChange, focusMinutes = 50, }) {
    const [planning, setPlanning] = useState(false);
    const [picks, setPicks] = useState(() => new Set());
    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [warning, setWarning] = useState(null);
    const [minutes, setMinutes] = useState(focusMinutes);
    const [focusEndsAt, setFocusEndsAt] = useState(null);
    const [focusDone, setFocusDone] = useState(null);
    const [nowTick, setNowTick] = useState(() => Date.now());
    const plan = data?.plan || null;
    const streak = data?.streak || 0;
    // Focus countdown.
    useEffect(() => {
        if (!focusEndsAt)
            return undefined;
        const t = window.setInterval(() => setNowTick(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, [focusEndsAt]);
    const focusLeft = focusEndsAt ? Math.max(0, focusEndsAt - nowTick) : 0;
    useEffect(() => {
        if (focusEndsAt && focusLeft === 0) {
            // Natural expiry: the block completed — say so.
            setFocusEndsAt(null);
            setFocusDone(minutes);
            onFocusChange?.(false);
        }
    }, [focusEndsAt, focusLeft, minutes, onFocusChange]);
    const startFocus = useCallback(() => {
        setFocusDone(null);
        setFocusEndsAt(Date.now() + minutes * 60000);
        onFocusChange?.(true);
    }, [minutes, onFocusChange]);
    const endFocus = useCallback(() => {
        // Ending early is a choice, not a completion — no end-moment.
        setFocusEndsAt(null);
        onFocusChange?.(false);
    }, [onFocusChange]);
    const cycleMinutes = useCallback(() => {
        setMinutes((m) => {
            const i = FOCUS_PRESETS.indexOf(m);
            return FOCUS_PRESETS[(i + 1) % FOCUS_PRESETS.length] || FOCUS_PRESETS[0];
        });
    }, []);
    const togglePick = useCallback((id) => {
        setPicks((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else if (next.size < MAX_PICKS)
                next.add(id);
            return next;
        });
    }, []);
    // The picker's visible pool: current picks first (when editing), then the
    // suggested slate. Search reaches the whole open pool beyond both.
    const pool = useMemo(() => {
        const seen = new Set();
        const out = [];
        const add = (t) => {
            if (t && !seen.has(t.id)) {
                seen.add(t.id);
                out.push(t);
            }
        };
        (plan?.tasks || []).filter((t) => t.status !== 'completed').forEach(add);
        (data?.suggestions || []).forEach(add);
        return out;
    }, [plan, data]);
    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q)
            return [];
        const shown = new Set(pool.map((t) => t.id));
        return (tasks || [])
            .filter((t) => t.status === 'pending' && !t.archived_at && !shown.has(t.id))
            .filter((t) => t.title.toLowerCase().includes(q)
            || (t.account_label || '').toLowerCase().includes(q))
            .slice(0, SEARCH_LIMIT);
    }, [query, pool, tasks]);
    // Every task the picker can currently see — proactive count and titles
    // resolve against this, not just the slate.
    const pickable = useMemo(() => {
        const m = new Map();
        [...pool, ...matches, ...(tasks || [])].forEach((t) => { if (!m.has(t.id))
            m.set(t.id, t); });
        return m;
    }, [pool, matches, tasks]);
    const pickedProactive = useMemo(() => [...picks].filter((id) => { const t = pickable.get(id); return t && isProactive(t); }).length, [picks, pickable]);
    const openPicker = useCallback((seed) => {
        setPicks(new Set((seed || []).filter((t) => t.status !== 'completed').map((t) => t.id)));
        setQuery('');
        setWarning(null);
        setPlanning(true);
    }, []);
    const commit = useCallback(() => {
        if (!onSetToday || !picks.size || busy)
            return;
        setBusy(true);
        void Promise.resolve(onSetToday([...picks]))
            .then((r) => {
            const w = r?.warning;
            setWarning(w || null);
            setPlanning(false);
            setPicks(new Set());
            setQuery('');
        })
            // Keep the picker open AND say why — a swallowed failure reads as a
            // button that does nothing.
            .catch(() => setWarning("Couldn't save the plan — try again"))
            .finally(() => setBusy(false));
    }, [onSetToday, picks, busy]);
    const mmss = (ms) => {
        const s = Math.ceil(ms / 1000);
        return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    };
    if (loading && !data)
        return null;
    const focusTitle = `Start a ${minutes}-minute focus block: every task board narrows to proactive work until it ends`;
    // ── Focus running: the bar IS the block ─────────────────────────────────
    // role="timer" (aria-live off) — a ticking clock must not be announced
    // every second.
    if (focusEndsAt) {
        return (_jsxs("div", { className: "trd trd-focus", role: "timer", "aria-label": "Focus block remaining", children: [_jsx(Flame, { size: 14, "aria-hidden": "true" }), _jsx("span", { className: "trd-focus-label", children: "Focus block \u2014 proactive work only" }), _jsx("span", { className: "trd-focus-clock", children: mmss(focusLeft) }), _jsxs("button", { type: "button", className: "trd-act", onClick: endFocus, title: "End the block early", children: [_jsx(Square, { size: 11 }), " End block"] })] }));
    }
    // ── Focus ended naturally: the end-moment ───────────────────────────────
    if (focusDone) {
        return (_jsxs("div", { className: "trd trd-focus", role: "status", children: [_jsx(Flame, { size: 14, "aria-hidden": "true" }), _jsxs("span", { className: "trd-focus-label", children: ["Focus block done \u2014 ", focusDone, " minutes of proactive work"] }), _jsx("button", { type: "button", className: "trd-act", onClick: () => setFocusDone(null), "aria-label": "Dismiss", children: _jsx(X, { size: 11 }) })] }));
    }
    // ── Planning: fresh plan or editing the existing one ────────────────────
    if (planning) {
        const sugg = (t, reason) => (_jsxs("button", { type: "button", className: `trd-sugg ${picks.has(t.id) ? 'trd-sugg-on' : ''}`, "aria-pressed": picks.has(t.id), onClick: () => togglePick(t.id), title: t.title, children: [picks.has(t.id) ? _jsx(Check, { size: 11 }) : null, _jsx("span", { className: "trd-pick-title", children: t.title }), reason && _jsx("span", { className: "trd-sugg-why", children: reason }), isProactive(t) && _jsx("span", { className: "trd-pick-pro", title: "Proactive \u2014 self-directed or goal-linked", children: "\u25CF" })] }, t.id));
        return (_jsxs("div", { className: "trd", role: "region", "aria-label": "Plan today", children: [_jsx(Sun, { size: 14, "aria-hidden": "true" }), _jsxs("div", { className: "trd-picker", children: [_jsxs("span", { className: "trd-prompt", children: ["Pick up to ", MAX_PICKS, " \u2014 at least one proactive (\u25CF):"] }), pool.map((t) => sugg(t, slateReason(t))), tasks && tasks.length > 0 && (_jsx("input", { className: "trd-search", type: "search", value: query, onChange: (e) => setQuery(e.target.value), placeholder: "search all open tasks\u2026", "aria-label": "Search all open tasks" })), matches.map((t) => sugg(t, t.account_label || null)), query.trim() && !matches.length && (_jsx("span", { className: "trd-prompt", children: "no match" })), picks.size > 0 && pickedProactive === 0 && (_jsx("span", { className: "trd-warn", children: "no proactive pick yet" })), _jsx("button", { type: "button", className: "trd-act trd-act-go", disabled: !picks.size || busy, onClick: commit, children: busy ? _jsx(Loader2, { size: 12, className: "trd-spin" }) : (plan ? `Update plan (${picks.size})` : `Start the day (${picks.size})`) }), _jsx("button", { type: "button", className: "trd-act", "aria-label": "Cancel planning", onClick: () => { setPlanning(false); setPicks(new Set()); setQuery(''); }, children: _jsx(X, { size: 11 }) })] }), warning && _jsx("span", { className: "trd-warn", children: warning })] }));
    }
    // ── Planned: the contract, live ─────────────────────────────────────────
    if (plan) {
        // Derived optimistic streak: a completed proactive pick today, before
        // the server has counted it, lights the flame at +1. The refetch lands
        // on the same number — nothing to roll back.
        const doneProactive = plan.tasks.some((t) => t.status === 'completed' && isProactive(t));
        const lit = doneProactive && data?.streak_today === false;
        const shownStreak = streak + (lit ? 1 : 0);
        return (_jsxs("div", { className: "trd", role: "region", "aria-label": "Today", children: [_jsx(Sun, { size: 14, "aria-hidden": "true" }), _jsxs("span", { className: "trd-progress", title: "Today's picks \u2014 done / picked", children: [plan.done, "/", plan.picked, " today"] }), plan.tasks.map((t) => (_jsxs("span", { className: `trd-pick ${t.status === 'completed' ? 'trd-pick-done' : ''}`, children: [t.status !== 'completed' && onComplete && (_jsx("button", { type: "button", className: "trd-pick-check", title: "Done", onClick: () => void Promise.resolve(onComplete(t.id)), children: _jsx(Check, { size: 11 }) })), _jsx("span", { className: "trd-pick-title", title: t.title, children: t.title }), isProactive(t) && _jsx("span", { className: "trd-pick-pro", title: "Proactive \u2014 self-directed or goal-linked", children: "\u25CF" })] }, t.id))), onSetToday && (_jsx("button", { type: "button", className: "trd-act", onClick: () => openPicker(plan.tasks), title: "Edit today's plan \u2014 add, remove, or swap picks", "aria-label": "Edit today's plan", children: _jsx(Pencil, { size: 11 }) })), plan.proactive_picked === 0 && (_jsx("span", { className: "trd-warn", title: "Every slot today serves someone else's agenda \u2014 the 30% floor loses by default when the plan starts this way.", children: "no proactive pick" })), shownStreak > 0 && (_jsxs("span", { className: `trd-streak ${lit ? 'trd-streak-lit' : ''}`, title: lit
                        ? `${shownStreak} — today's proactive completion keeps the streak alive`
                        : `${shownStreak} consecutive day${shownStreak === 1 ? '' : 's'} with a completed proactive task — a completion today keeps it alive`, children: [_jsx(Flame, { size: 12 }), " ", shownStreak] })), onFocusChange && (_jsxs("span", { className: "trd-focus-ctl", children: [_jsxs("button", { type: "button", className: "trd-act trd-act-go", onClick: startFocus, title: focusTitle, children: [_jsx(Play, { size: 11 }), " Focus"] }), _jsxs("button", { type: "button", className: "trd-act trd-min", onClick: cycleMinutes, title: "Block length \u2014 click to change", "aria-label": `Focus block length ${minutes} minutes — click to change`, children: [minutes, "m"] })] })), warning && _jsx("span", { className: "trd-warn", children: warning })] }));
    }
    // ── Unplanned: the ritual prompt ────────────────────────────────────────
    return (_jsxs("div", { className: "trd", role: "region", "aria-label": "Plan today", children: [_jsx(Sun, { size: 14, "aria-hidden": "true" }), _jsx("span", { className: "trd-prompt", children: "No plan for today yet." }), streak > 0 && (_jsxs("span", { className: "trd-streak", title: `${streak}-day proactive streak on the line`, children: [_jsx(Flame, { size: 12 }), " ", streak] })), onSetToday && (_jsx("button", { type: "button", className: "trd-act trd-act-go", onClick: () => openPicker(null), children: "Plan today" })), warning && _jsx("span", { className: "trd-warn", children: warning })] }));
}
export default TodayBar;
